import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { BookOpen, CheckCircle2, Loader2, Sparkles, Tag, Link2, X } from "lucide-react";
import { createLibraryItem, hyperlinkTooLong, LIBRARY_LIST_ID, MAX_HYPERLINK_LENGTH } from "../api/graph";
import type { NewLibraryFields } from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import { useListColumns, mergeChoices, hintFor } from "../hooks/useListColumns";
import type { LibraryItem } from "../types/navigator";
import { ConfirmDialog } from "./ConfirmDialog";

type Props = {
  /** Existing library items — used to suggest choice values for dropdowns
   *  so we don't have to hardcode them. */
  existing: LibraryItem[];
  onClose: () => void;
  onCreated: () => void;
};

type FormState = {
  Title: string;
  Description: string;
  AssetType: string;
  AIPlatform: string;
  Complexity: string;
  BenefitType: string;
  Instructions: string;
  Department: string;
  LicenseRequired: string;
  UrlHref: string;
  UrlLabel: string;
};

const EMPTY: FormState = {
  Title: "",
  Description: "",
  AssetType: "",
  AIPlatform: "",
  Complexity: "",
  BenefitType: "",
  Instructions: "",
  Department: "",
  LicenseRequired: "",
  UrlHref: "",
  UrlLabel: "",
};

const LOCKED_STATUS = "Under Review";
const AUTO_CLOSE_MS = 2400;

function uniqueValues(items: LibraryItem[], pick: (i: LibraryItem) => string): string[] {
  const set = new Set<string>();
  for (const item of items) {
    const v = pick(item).trim();
    if (v) set.add(v);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function AddLibraryItemModal({ existing, onClose, onCreated }: Props) {
  const getToken = useGraphToken();
  const { columns } = useListColumns(LIBRARY_LIST_ID);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Budget shrinks if a custom label is typed — Description defaults to
  // "Open" when blank, matching hyperlinkTooLong()'s own fallback.
  const urlMaxChars = MAX_HYPERLINK_LENGTH - (form.UrlLabel.trim() || "Open").length;

  // Suggestions merge SharePoint's configured Choice values with anything
  // already in use on existing rows (covers legacy values SharePoint's
  // column no longer lists), so the datalist always offers the full set.
  const assetTypes = useMemo(
    () => mergeChoices(columns, "AssetType", uniqueValues(existing, (i) => i.assetType)),
    [columns, existing]
  );
  const platforms = useMemo(
    () => mergeChoices(columns, "AIPlatform", uniqueValues(existing, (i) => i.aiPlatform)),
    [columns, existing]
  );
  const complexities = useMemo(
    () => mergeChoices(columns, "Complexity", uniqueValues(existing, (i) => i.complexity)),
    [columns, existing]
  );
  const benefits = useMemo(
    () => mergeChoices(columns, "BenefitType", uniqueValues(existing, (i) => i.benefitType)),
    [columns, existing]
  );
  const departments = useMemo(
    () => mergeChoices(columns, "Department", uniqueValues(existing, (i) => i.department)),
    [columns, existing]
  );
  const licenses = useMemo(
    () => mergeChoices(columns, "LicenseRequired", uniqueValues(existing, (i) => i.license)),
    [columns, existing]
  );

  // Unsaved work exists while the form differs from its empty starting state
  // and hasn't been submitted yet. We compare a serialized snapshot so any
  // typed field — including the optional ones — counts.
  const dirty = !submitted && JSON.stringify(form) !== JSON.stringify(EMPTY);

  // Guarded dismiss: prompt before discarding unsaved edits. A save in progress
  // blocks dismissal entirely (matches the existing behavior).
  const requestClose = useCallback(() => {
    if (submitting) return;
    if (dirty) setConfirmOpen(true);
    else onClose();
  }, [submitting, dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting && !confirmOpen) requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestClose, submitting, confirmOpen]);

  useEffect(() => {
    if (!submitted) return;
    const t = window.setTimeout(() => onCreated(), AUTO_CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [submitted, onCreated]);

  const update = (key: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canSubmit =
    form.Title.trim().length > 0 &&
    form.Description.trim().length > 0 &&
    form.AssetType.trim().length > 0 &&
    form.AIPlatform.trim().length > 0 &&
    !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    if (
      form.UrlHref.trim() &&
      hyperlinkTooLong(form.UrlHref.trim(), form.UrlLabel.trim())
    ) {
      setError(
        `Link URL is too long — SharePoint's Hyperlink field caps the URL and label combined at about ${MAX_HYPERLINK_LENGTH} characters. Try a shorter URL or a link-shortener.`
      );
      return;
    }
    setSubmitting(true);
    try {
      const token = await getToken();
      const payload: NewLibraryFields = {
        Title: form.Title.trim(),
        Description: form.Description.trim() || undefined,
        AssetType: form.AssetType.trim() || undefined,
        AIPlatform: form.AIPlatform.trim() || undefined,
        Complexity: form.Complexity.trim() || undefined,
        BenefitType: form.BenefitType.trim() || undefined,
        Instructions: form.Instructions.trim() || undefined,
        Department: form.Department.trim() || undefined,
        LicenseRequired: form.LicenseRequired.trim() || undefined,
        AssetStatus: LOCKED_STATUS,
      };
      if (form.UrlHref.trim()) {
        payload.URL = {
          url: form.UrlHref.trim(),
          label: form.UrlLabel.trim() || undefined,
        };
      }
      await createLibraryItem(token, payload);
      setSubmitting(false);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop"
        onClick={(e) => {
          if (e.target === e.currentTarget && !submitting) requestClose();
        }}
      >
        <div className="modal modal-library add-item-modal" role="dialog" aria-modal="true">
          <div className="modal-accent" aria-hidden />
          <div className="modal-head">
            <div className="modal-head-icon icon-library" aria-hidden>
              <BookOpen size={22} />
            </div>
            <div className="modal-head-text">
              <span className="modal-kind">AI Library</span>
              <h2 className="modal-title">
                {submitted ? "Submission received" : "Add new library item"}
              </h2>
              {!submitted ? (
                <p className="add-item-subtitle">
                  Tell us about your asset. Entries are added as{" "}
                  <strong>Under Review</strong> and published after review.
                </p>
              ) : null}
            </div>
            {!submitted ? (
              <button
                type="button"
                className="modal-close"
                onClick={requestClose}
                disabled={submitting}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            ) : null}
          </div>

          {submitted ? (
            <div className="add-item-success" role="status" aria-live="polite">
              <div className="add-item-success-icon" aria-hidden>
                <CheckCircle2 size={40} strokeWidth={2.2} />
              </div>
              <h3 className="add-item-success-title">Thanks — your entry is in the queue</h3>
              <p className="add-item-success-body">
                Your submission was saved successfully. A reviewer will check it over
                and publish it to the AI Library shortly.
              </p>
              <button
                type="button"
                className="btn btn-primary add-item-success-done"
                onClick={onCreated}
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <form className="modal-body add-item-form" onSubmit={onSubmit}>
                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <Sparkles size={14} aria-hidden />
                    <h3>The basics</h3>
                  </header>
                  <p className="add-item-section-sub">
                    Help others discover what this asset is and what problem it solves.
                  </p>

                  <Field
                    label="Title"
                    required
                    hint="A short, descriptive name. Example: “Meeting recap prompt for Copilot”."
                  >
                    <input
                      type="text"
                      value={form.Title}
                      onChange={(e) => update("Title", e.target.value)}
                      required
                      maxLength={255}
                      autoFocus
                      placeholder={hintFor(columns, "Title", "What is this asset called?")}
                    />
                  </Field>

                  <Field
                    label="Description"
                    required
                    hint="One or two sentences on what it does and when someone should use it."
                  >
                    <textarea
                      rows={3}
                      value={form.Description}
                      onChange={(e) => update("Description", e.target.value)}
                      placeholder={hintFor(columns, "Description", "A quick summary reviewers and users will see in the library.")}
                    />
                  </Field>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <Tag size={14} aria-hidden />
                    <h3>Classify it</h3>
                  </header>
                  <p className="add-item-section-sub">
                    These tags power filters and search. Pick from past entries or type your own.
                  </p>

                  <div className="add-item-row">
                    <Field
                      label="Asset type"
                      required
                      hint="What kind of thing is this? e.g. Prompt, Agent, Workflow, GPT."
                    >
                      <DataListInput
                        value={form.AssetType}
                        onChange={(v) => update("AssetType", v)}
                        options={assetTypes}
                        listId="li-asset-type"
                        placeholder={hintFor(columns, "AssetType", "Prompt, Agent, Workflow…")}
                      />
                    </Field>
                    <Field
                      label="Platform"
                      required
                      hint="Where does it run? e.g. Copilot, ChatGPT, Gemini, Claude."
                    >
                      <DataListInput
                        value={form.AIPlatform}
                        onChange={(v) => update("AIPlatform", v)}
                        options={platforms}
                        listId="li-ai-platform"
                        placeholder={hintFor(columns, "AIPlatform", "Copilot, ChatGPT…")}
                      />
                    </Field>
                  </div>

                  <div className="add-item-row">
                    <Field
                      label="Complexity"
                      hint="How much effort to set up and use? Low / Medium / High."
                    >
                      <DataListInput
                        value={form.Complexity}
                        onChange={(v) => update("Complexity", v)}
                        options={complexities}
                        listId="li-complexity"
                        placeholder={hintFor(columns, "Complexity", "Low, Medium, High")}
                      />
                    </Field>
                    <Field
                      label="Benefit type"
                      hint="The main value it delivers — time saved, quality, automation, insight."
                    >
                      <DataListInput
                        value={form.BenefitType}
                        onChange={(v) => update("BenefitType", v)}
                        options={benefits}
                        listId="li-benefit"
                        placeholder={hintFor(columns, "BenefitType", "Time saved, Quality…")}
                      />
                    </Field>
                  </div>

                  <div className="add-item-row">
                    <Field
                      label="Department"
                      hint="Which team is this most useful for? Leave blank if it's broadly applicable."
                    >
                      <DataListInput
                        value={form.Department}
                        onChange={(v) => update("Department", v)}
                        options={departments}
                        listId="li-department"
                        placeholder={hintFor(columns, "Department", "Finance, HR, IT…")}
                      />
                    </Field>
                    <Field
                      label="License"
                      hint="Any paid plan or license needed? Note it here so users know up front."
                    >
                      <DataListInput
                        value={form.LicenseRequired}
                        onChange={(v) => update("LicenseRequired", v)}
                        options={licenses}
                        listId="li-license"
                        placeholder={hintFor(columns, "LicenseRequired", "None, Copilot Pro…")}
                      />
                    </Field>
                  </div>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <BookOpen size={14} aria-hidden />
                    <h3>How to use it</h3>
                  </header>
                  <p className="add-item-section-sub">
                    Walk a new user through getting value from this asset.
                  </p>

                  <Field
                    label="Instructions"
                    hint="Step-by-step usage notes, prompt text, or example inputs."
                  >
                    <textarea
                      rows={4}
                      value={form.Instructions}
                      onChange={(e) => update("Instructions", e.target.value)}
                      placeholder={hintFor(columns, "Instructions", "How should someone use this asset? Paste the prompt or steps.")}
                    />
                  </Field>
                </section>

                <section className="add-item-section">
                  <header className="add-item-section-head">
                    <Link2 size={14} aria-hidden />
                    <h3>Link (optional)</h3>
                  </header>
                  <p className="add-item-section-sub">
                    If this lives somewhere — a Copilot agent, doc, or workspace — link it.
                  </p>

                  <div className="add-item-row">
                    <Field
                      label="Link URL"
                      hint="The full https:// link people should open."
                      labelRight={
                        <span
                          className={`add-item-charcount${
                            form.UrlHref.trim().length > urlMaxChars ? " is-over" : ""
                          }`}
                        >
                          {form.UrlHref.trim().length}/{urlMaxChars} — cannot exceed {urlMaxChars} characters
                        </span>
                      }
                    >
                      <input
                        type="url"
                        value={form.UrlHref}
                        onChange={(e) => update("UrlHref", e.target.value)}
                        placeholder="https://…"
                      />
                    </Field>
                    <Field
                      label="Link label"
                      hint="What the link button should say. Defaults to “Open”."
                    >
                      <input
                        type="text"
                        value={form.UrlLabel}
                        onChange={(e) => update("UrlLabel", e.target.value)}
                        placeholder="e.g. Open in Copilot"
                      />
                    </Field>
                  </div>
                </section>

                {error ? (
                  <div className="add-item-error" role="alert">
                    <strong>Couldn't save.</strong> {error}
                  </div>
                ) : null}
              </form>

              <div className="modal-foot">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canSubmit}
                  onClick={onSubmit}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="add-item-spin" /> Saving…
                    </>
                  ) : (
                    "Submit for review"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Discard your changes?"
        message="You've started filling this out but haven't submitted yet. If you leave now, your entries will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        tone="danger"
        autoFocusConfirm={false}
        onConfirm={() => {
          setConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

function Field({
  label,
  required,
  hint,
  labelRight,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  labelRight?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="add-item-field">
      <span className="add-item-label-row">
        <span className="add-item-label">
          {label}
          {required ? <span aria-hidden className="add-item-required">*</span> : null}
        </span>
        {labelRight}
      </span>
      {children}
      {hint ? <span className="add-item-hint">{hint}</span> : null}
    </label>
  );
}

function DataListInput({
  value,
  onChange,
  options,
  listId,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  listId: string;
  placeholder?: string;
}) {
  return (
    <>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        list={options.length ? listId : undefined}
        placeholder={placeholder}
      />
      {options.length ? (
        <datalist id={listId}>
          {options.map((opt) => (
            <option key={opt} value={opt} />
          ))}
        </datalist>
      ) : null}
    </>
  );
}
